import React, { useState, useEffect, useRef } from 'react';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import type { InternalChatConversation, InternalMessage, User, AgentStatus } from '../types';
import { socket } from '../services/socket';
import {
  MessageCircle,
  Send,
  Search,
  Plus,
  X,
  Circle,
  Paperclip,
  FileText,
  Image as ImageIcon,
  Video
} from 'lucide-react';

const statusConfig: Record<AgentStatus, { label: string; color: string }> = {
  ONLINE: { label: 'Online', color: 'text-green-600' },
  AUSENTE: { label: 'Ausente', color: 'text-yellow-600' },
  OCUPADO: { label: 'Ocupado', color: 'text-red-600' },
  EM_REUNIAO: { label: 'Em Reunião', color: 'text-purple-600' },
  OFFLINE: { label: 'Offline', color: 'text-gray-600' },
};

export const InternalChat: React.FC = () => {
  const { agent } = useAuth();
  const [conversations, setConversations] = useState<InternalChatConversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<InternalChatConversation | null>(null);
  const [messages, setMessages] = useState<InternalMessage[]>([]);
  const [messageInput, setMessageInput] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const [availableAgents, setAvailableAgents] = useState<User[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Load conversations once on mount
    loadConversations();
  }, []);

  useEffect(() => {
    if (selectedConversation) {
      loadMessages(selectedConversation.agent.id);
    }
  }, [selectedConversation]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Listen for agent status updates via WebSocket
  useEffect(() => {
    const handleAgentStatusUpdate = (data: { agentId: string; status: AgentStatus }) => {
      console.log('👤 [InternalChat] Agent status update received:', data);
      console.log('👤 [InternalChat] Current selected conversation agent ID:', selectedConversation?.agent.id);

      // Update conversations list with new status
      setConversations((prevConversations) =>
        prevConversations.map((conv) =>
          conv.agent.id === data.agentId
            ? { ...conv, agent: { ...conv.agent, status: data.status } }
            : conv
        )
      );

      // Update selected conversation status if it matches (use callback to get latest value)
      setSelectedConversation((prevSelected) => {
        if (prevSelected?.agent.id === data.agentId) {
          console.log('👤 [InternalChat] ✅ Updating selected conversation status to:', data.status);
          return {
            ...prevSelected,
            agent: { ...prevSelected.agent, status: data.status },
          };
        }
        console.log('👤 [InternalChat] ❌ Not updating - agent ID does not match');
        return prevSelected;
      });

      // Update available agents list in modal
      setAvailableAgents((prevAgents) =>
        prevAgents.map((agent) =>
          agent.id === data.agentId ? { ...agent, status: data.status } : agent
        )
      );
    };

    socket.on('agent-status-updated', handleAgentStatusUpdate);

    return () => {
      socket.off('agent-status-updated', handleAgentStatusUpdate);
    };
  }, [selectedConversation]);

  // Listen for new internal messages via WebSocket
  useEffect(() => {
    const handleInternalMessage = (data: { senderId: string; receiverId: string; message: InternalMessage }) => {
      console.log('💬 [InternalChat] Internal message event received:', data);

      // Only process if message involves current agent
      if (data.senderId === agent?.id || data.receiverId === agent?.id) {
        console.log('💬 [InternalChat] Message involves me - reloading conversations');

        // Reload conversations to update last message and unread count
        loadConversations();

        // If message is for current selected conversation, add it to messages
        if (selectedConversation) {
          const otherAgentId = data.senderId === agent?.id ? data.receiverId : data.senderId;

          if (otherAgentId === selectedConversation.agent.id) {
            console.log('💬 [InternalChat] Message is for current conversation - adding to messages');
            setMessages((prevMessages) => [...prevMessages, data.message]);
          }
        }
      }
    };

    socket.on('internal-message-sent', handleInternalMessage);

    return () => {
      socket.off('internal-message-sent', handleInternalMessage);
    };
  }, [agent?.id, selectedConversation]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadConversations = async () => {
    try {
      const data = await api.getInternalChatConversations();
      setConversations(data);
    } catch (error) {
      console.error('Failed to load conversations:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadMessages = async (otherAgentId: string) => {
    try {
      const data = await api.getInternalMessages(otherAgentId);
      setMessages(data);
    } catch (error) {
      console.error('Failed to load messages:', error);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const MAX_SIZE_MB = 10;
      const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;

      // Check file size (10MB limit)
      if (file.size > MAX_SIZE_BYTES) {
        const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
        alert(
          `❌ Arquivo muito grande!\n\n` +
          `Tamanho do arquivo: ${fileSizeMB} MB\n` +
          `Tamanho máximo permitido: ${MAX_SIZE_MB} MB\n\n` +
          `Por favor, selecione um arquivo menor.`
        );
        // Clear the input
        if (e.target) {
          e.target.value = '';
        }
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if ((!messageInput.trim() && !selectedFile) || !selectedConversation) return;

    setIsUploading(true);
    try {
      let messageType: 'TEXT' | 'IMAGE' | 'VIDEO' | 'AUDIO' | 'DOCUMENT' = 'TEXT';
      let mediaUrl: string | undefined;
      let originalFilename: string | undefined;
      let messageContent = messageInput.trim();

      // Upload file if present
      if (selectedFile) {
        const uploadResponse = await api.uploadFile(selectedFile);
        mediaUrl = uploadResponse.fileUrl;
        originalFilename = selectedFile.name;

        // Determine message type based on file MIME type
        if (selectedFile.type.startsWith('image/')) {
          messageType = 'IMAGE';
          messageContent = messageContent || '[Imagem]';
        } else if (selectedFile.type.startsWith('video/')) {
          messageType = 'VIDEO';
          messageContent = messageContent || '[Vídeo]';
        } else if (selectedFile.type.startsWith('audio/')) {
          messageType = 'AUDIO';
          messageContent = messageContent || '[Áudio]';
        } else {
          messageType = 'DOCUMENT';
          messageContent = messageContent || selectedFile.name;
        }
      }

      const newMessage = await api.sendInternalMessage(
        selectedConversation.agent.id,
        messageContent,
        messageType,
        mediaUrl,
        originalFilename
      );

      setMessages([...messages, newMessage]);
      setMessageInput('');
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      await loadConversations(); // Refresh conversation list
    } catch (error: any) {
      console.error('Failed to send message:', error);

      // Check if it's a file size error from the server
      if (error.response?.status === 413 || error.response?.data?.error === 'Arquivo muito grande') {
        alert(
          `❌ Erro ao enviar arquivo!\n\n` +
          `${error.response?.data?.details || 'O arquivo é muito grande.'}\n` +
          `Tamanho máximo: ${error.response?.data?.maxSize || '10MB'}`
        );
      } else {
        alert('Falha ao enviar mensagem. Tente novamente.');
      }
    } finally {
      setIsUploading(false);
    }
  };

  const handleSelectConversation = (conversation: InternalChatConversation) => {
    setSelectedConversation(conversation);
    loadMessages(conversation.agent.id);
  };

  const handleOpenNewChatModal = async () => {
    try {
      const agents = await api.getAvailableAgentsForChat();
      setAvailableAgents(agents);
      setShowNewChatModal(true);
    } catch (error) {
      console.error('Failed to load agents:', error);
    }
  };

  const handleStartNewChat = (agentToChat: User) => {
    // Create a temporary conversation
    const newConversation: InternalChatConversation = {
      agent: {
        id: agentToChat.id,
        name: agentToChat.name,
        email: agentToChat.email,
        role: agentToChat.role,
        department: agentToChat.department,
      },
      lastMessage: null,
      unreadCount: 0,
    };
    setSelectedConversation(newConversation);
    setMessages([]);
    setShowNewChatModal(false);
  };

  const filteredAgents = availableAgents.filter((agent) =>
    agent.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    agent.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-8rem)]">
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Chat Interno</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">Converse com outros usuários do sistema</p>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow h-[calc(100%-5rem)] flex">
        {/* Conversations List */}
        <div className="w-80 border-r border-gray-200 dark:border-gray-700 flex flex-col">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <button
              onClick={handleOpenNewChatModal}
              className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition flex items-center justify-center space-x-2"
            >
              <Plus className="w-4 h-4" />
              <span>Nova Conversa</span>
            </button>
          </div>

          <div className="flex-1 overflow-y-auto">
            {conversations.length === 0 ? (
              <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                <MessageCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p className="text-sm">Nenhuma conversa ainda</p>
                <p className="text-xs mt-1">Clique em "Nova Conversa" para começar</p>
              </div>
            ) : (
              conversations.map((conv) => (
                <button
                  key={conv.agent.id}
                  onClick={() => handleSelectConversation(conv)}
                  className={`w-full p-4 border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition text-left ${
                    selectedConversation?.agent.id === conv.agent.id
                      ? 'bg-blue-50 dark:bg-blue-900/20'
                      : ''
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-gray-900 dark:text-white">{conv.agent.name}</span>
                      <Circle className={`w-2 h-2 fill-current ${conv.agent.status ? statusConfig[conv.agent.status]?.color : 'text-gray-400'}`} />
                    </div>
                    {conv.unreadCount > 0 && (
                      <span className="px-2 py-1 bg-blue-600 text-white text-xs rounded-full">
                        {conv.unreadCount}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mb-1">
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {conv.agent.department?.name || 'Sem departamento'}
                    </p>
                    <span className={`text-xs ${conv.agent.status ? statusConfig[conv.agent.status]?.color : 'text-gray-400'}`}>
                      • {conv.agent.status ? statusConfig[conv.agent.status]?.label : 'Offline'}
                    </span>
                  </div>
                  {conv.lastMessage && (
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm text-gray-600 dark:text-gray-300 truncate flex-1">
                        {conv.lastMessage.senderId === agent?.id ? 'Você: ' : ''}
                        {conv.lastMessage.content}
                      </p>
                      <span className="text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap">
                        {new Date(conv.lastMessage.createdAt).toLocaleString('pt-BR', {
                          day: '2-digit',
                          month: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </span>
                    </div>
                  )}
                </button>
              ))
            )}
          </div>
        </div>

        {/* Messages Area */}
        <div className="flex-1 flex flex-col">
          {selectedConversation ? (
            <>
              {/* Header */}
              <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                      {selectedConversation.agent.name}
                      <Circle className={`w-2 h-2 fill-current ${selectedConversation.agent.status ? statusConfig[selectedConversation.agent.status]?.color : 'text-gray-400'}`} />
                    </h2>
                    <p className={`text-xs ${selectedConversation.agent.status ? statusConfig[selectedConversation.agent.status]?.color : 'text-gray-400'}`}>
                      {selectedConversation.agent.status ? statusConfig[selectedConversation.agent.status]?.label : 'Offline'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="hidden">
                <h2 className="font-semibold text-gray-900 dark:text-white">
                  {selectedConversation.agent.name}
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {selectedConversation.agent.email}
                  {selectedConversation.agent.department && ` • ${selectedConversation.agent.department.name}`}
                </p>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {messages.length === 0 ? (
                  <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
                    <div className="text-center">
                      <MessageCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>Nenhuma mensagem ainda</p>
                      <p className="text-sm mt-1">Envie a primeira mensagem!</p>
                    </div>
                  </div>
                ) : (
                  messages.map((message) => {
                    const isSent = message.senderId === agent?.id;
                    return (
                      <div
                        key={message.id}
                        className={`flex ${isSent ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-md px-4 py-2 rounded-lg ${
                            isSent
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white'
                          }`}
                        >
                          {/* Render media based on messageType */}
                          {message.messageType === 'IMAGE' && message.mediaUrl && (
                            <div className="mb-2">
                              <img
                                src={message.mediaUrl}
                                alt="Imagem"
                                className="max-w-full rounded cursor-pointer"
                                onClick={() => window.open(message.mediaUrl, '_blank')}
                              />
                            </div>
                          )}
                          {message.messageType === 'VIDEO' && message.mediaUrl && (
                            <div className="mb-2">
                              <video
                                controls
                                className="max-w-full rounded"
                                src={message.mediaUrl}
                              >
                                Seu navegador não suporta vídeos.
                              </video>
                            </div>
                          )}
                          {message.messageType === 'DOCUMENT' && message.mediaUrl && (
                            <a
                              href={message.mediaUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`flex items-center space-x-2 mb-2 ${
                                isSent ? 'text-white hover:text-blue-100' : 'text-blue-600 hover:text-blue-800'
                              }`}
                            >
                              <FileText className="w-5 h-5" />
                              <span className="underline">{message.originalFilename || 'Documento'}</span>
                            </a>
                          )}

                          <p className="break-words">{message.content}</p>
                          <p
                            className={`text-xs mt-1 ${
                              isSent ? 'text-blue-100' : 'text-gray-500 dark:text-gray-400'
                            }`}
                          >
                            {new Date(message.createdAt).toLocaleTimeString('pt-BR', {
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </p>
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <form onSubmit={handleSendMessage} className="p-4 border-t border-gray-200 dark:border-gray-700">
                {/* File Preview */}
                {selectedFile && (
                  <div className="mb-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      {selectedFile.type.startsWith('image/') ? (
                        <ImageIcon className="w-5 h-5 text-blue-600" />
                      ) : selectedFile.type.startsWith('video/') ? (
                        <Video className="w-5 h-5 text-blue-600" />
                      ) : (
                        <FileText className="w-5 h-5 text-blue-600" />
                      )}
                      <span className="text-sm text-gray-700 dark:text-gray-300 truncate max-w-xs">
                        {selectedFile.name}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={handleRemoveFile}
                      className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                    >
                      <X className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                    </button>
                  </div>
                )}

                <div className="flex items-center space-x-2">
                  {/* Hidden file input */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    onChange={handleFileSelect}
                    className="hidden"
                    accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                  />

                  {/* Attach file button */}
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isUploading}
                    className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Anexar arquivo"
                  >
                    <Paperclip className="w-5 h-5" />
                  </button>

                  <input
                    type="text"
                    value={messageInput}
                    onChange={(e) => setMessageInput(e.target.value)}
                    placeholder="Digite sua mensagem..."
                    className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck="false"
                    disabled={isUploading}
                  />
                  <button
                    type="submit"
                    disabled={(!messageInput.trim() && !selectedFile) || isUploading}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-lg transition flex items-center space-x-2"
                  >
                    {isUploading ? (
                      <>
                        <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span>Enviando...</span>
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        <span>Enviar</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-gray-500 dark:text-gray-400">
              <div className="text-center">
                <MessageCircle className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p className="text-lg">Selecione uma conversa</p>
                <p className="text-sm mt-1">ou inicie uma nova conversa</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* New Chat Modal */}
      {showNewChatModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Nova Conversa</h2>
              <button
                onClick={() => setShowNewChatModal(false)}
                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
              >
                <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              </button>
            </div>

            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Buscar usuário..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>
            </div>

            <div className="max-h-96 overflow-y-auto space-y-2">
              {filteredAgents.length === 0 ? (
                <p className="text-center text-gray-500 dark:text-gray-400 py-4">
                  Nenhum usuário encontrado
                </p>
              ) : (
                filteredAgents.map((agentItem) => (
                  <button
                    key={agentItem.id}
                    onClick={() => handleStartNewChat(agentItem)}
                    className="w-full p-3 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition text-left"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-semibold text-gray-900 dark:text-white">{agentItem.name}</p>
                      <Circle className={`w-2 h-2 fill-current ${agentItem.status ? statusConfig[agentItem.status]?.color : 'text-gray-400'}`} />
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{agentItem.email}</p>
                    <div className="flex items-center gap-2 mt-1">
                      {agentItem.department && (
                        <p className="text-xs text-gray-400 dark:text-gray-500">
                          {agentItem.department.name}
                        </p>
                      )}
                      <span className={`text-xs ${agentItem.status ? statusConfig[agentItem.status]?.color : 'text-gray-400'}`}>
                        • {agentItem.status ? statusConfig[agentItem.status]?.label : 'Offline'}
                      </span>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
